import invariant from 'invariant';
import { always, curry, compose, reject, applySpec, o, ifElse, is, T, identity, map, nthArg, isNil, propEq, complement } from 'ramda';
import { isNilOrEmptyString, valueMirror, isPlainObject } from 'ramda-extension';
var alwaysUndefined = always(undefined);
var isUndefined = function isUndefined(value) {
  return value === undefined;
};
/**
 * Creates an action creator with supplied type and payload & meta getters.
 *
 * @sig String -> (a -> b) -> (a -> c) -> a -> {type: String, payload: b, meta: c}
 *
 * @example
 *
 *    const reset = makeEmptyActionCreator("RESET")
 *    const add = makePayloadActionCreator("ADD");
 *    const fetchItems = configureActionCreator("FETCH_ITEMS", R.prop("items"), R.always({ page: 0 }))
 */

var configureActionCreator = curry(function (type, getPayload, getMeta) {
  invariant(!isNilOrEmptyString(type), "Action type must be a non-empty string (received " + type + ")");
  var actionCreator = compose(reject(isUndefined), applySpec({
    type: always(type),
    payload: getPayload,
    meta: getMeta,
    error: o(ifElse(is(Error), T, alwaysUndefined), getPayload)
  }));
  return actionCreator;
});

/**
 * Creates an action creator with supplied type, no payload and no meta.
 *
 * @sig String -> () -> {type: String}
 */

var makeEmptyActionCreator = function makeEmptyActionCreator(type) {
  var actionCreator = configureActionCreator(type, alwaysUndefined, alwaysUndefined); // NOTE: Regular function so we can use `arguments` without entering any parameters.
  // An arrow function allows us to do `...args`, but that hurts autocompletion.

  return function () {
    invariant(arguments.length === 0, 'You passed an argument to an action creator created by makeEmptyActionCreator(' + type + '). Did you mean to use makePayloadActionCreator(' + type + ')?');
    return actionCreator(undefined);
  };
};

/**
 * Creates an action creator with supplied type. The resulting action payload is the first arg.
 *
 * @sig String -> a -> {type: String, payload: a}
 */

var makePayloadActionCreator = function makePayloadActionCreator(type) {
  var actionCreator = configureActionCreator(type, identity, alwaysUndefined);
  return function (payload) {
    invariant(arguments.length !== 0, 'You did not pass an argument to an action creator created by makePayloadActionCreator(' + type + '). Did you mean to use makeEmptyActionCreator(' + type + ')?');
    invariant(arguments.length <= 1, 'You passed more than one argument to an action creator created by makePayloadActionCreator(' + type + ').');
    return actionCreator(payload);
  };
};
var addPrefix = function addPrefix(prefix) {
  return map(function (x) {
    return prefix + "/" + x;
  });
};
/**
 * Works just like `valueMirror` from ramda-extension, but prefixes each value.
 *
 * @param {string} prefix a string to prefix each value with
 * @param {Array} xs array of values to mirror as keys
 * @returns {Object} object with values set to `prefix/key`
 */

var makeActionTypes = curry(function (prefix, xs) {
  return o(addPrefix(prefix), valueMirror)(xs);
});

/**
 * Creates a new binary action creator which will use the first argument as the payload and the second argument as the meta.
 *
 * @sig String -> (a, {}) -> {type: String, payload: a, meta: {}}
 */

var makePayloadMetaActionCreator = function makePayloadMetaActionCreator(type) {
  var actionCreator = configureActionCreator(type, nthArg(0), nthArg(1));
  return function (payload, meta) {
    invariant(!isNil(meta), 'You did not pass the meta object to an action creator created by makePayloadMetaActionCreator(' + type + ').');
    invariant(isPlainObject(meta), 'Action creator created by makePayloadMetaActionCreator(' + type + ') expects the meta argument to be a plain object. Instead, it received ' + meta + '.');
    return actionCreator(payload, meta);
  };
};

/**
 * Returns whether the action is an error action or not.
 *
 * @param {Object} action Redux action
 * @returns {boolean}
 */

var isErrorAction = propEq('error', true);

/**
 * Returns whether the action is NOT an error action.
 *
 * @param {Object} action Redux action
 * @returns {boolean}
 */

var isNotErrorAction = complement(isErrorAction);
var makeConstantActionCreator = makeEmptyActionCreator;
var makeSimpleActionCreator = makePayloadActionCreator;
export { configureActionCreator, isErrorAction, isNotErrorAction, makeActionTypes, makeConstantActionCreator, makeEmptyActionCreator, makePayloadActionCreator, makePayloadMetaActionCreator, makeSimpleActionCreator };